import moment from 'moment'

import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

import { PayrollPhase, PayrollStatus } from 'modules/payroll_bundle/lib/enums'
import type { PolicyPeriodAuthorizations } from 'modules/payroll_bundle/lib/types'

/**
 * PayrollStatus refers to the backend status in the DB. A PayrollPhase consists
 * of one or more PayrollStatuses and is used for presentation.
 */

export type PayrollPhaseDefineStatus = Extract<
  PayrollStatus,
  PayrollStatus.PREPARATION | PayrollStatus.SUPPLEMENTS_DEFINITION | PayrollStatus.UNDER_REVIEW
>

const payrollStatusVisualMapping: Record<PayrollStatus, PayrollPhase> = {
  preparation: PayrollPhase.SUPPLEMENTS_DEFINITION,
  supplements_definition: PayrollPhase.SUPPLEMENTS_DEFINITION,
  under_review: PayrollPhase.SUPPLEMENTS_DEFINITION,
  payroll_calculation: PayrollPhase.PAYROLL_CALCULATION,
  payment_processing: PayrollPhase.PAYMENT,
  paid: PayrollPhase.PAYMENT,
}

export function getPayrollPhase(status: PayrollStatus): PayrollPhase {
  return payrollStatusVisualMapping[status]
}

export type AvailablePeriods = 'current' | 'next'

// FIXME: The commented attributes are what should be used, but there are dozens of type errors
// that surface when updated. These are important as several lead to probable runtime errors, but
// there are too many to tackle immediately. This should be fixed in a separate PR.
interface IPolicyPeriod {
  // id: number
  // policy_id: number
  // starts_on: string
  // ends_on: string
  // status: Status
  // name: string
  // period: string
  [key: string]: any
}

interface StatusDescriptionObject {
  main_description: string
  modal_description: string
  additional_modal_description?: string
  notification?: string
}

type StatusDescription = Record<PayrollStatus, StatusDescriptionObject>

class PolicyPeriod extends Model<IPolicyPeriod> {
  static Statuses = (): { [status in PayrollStatus]: string } => ({
    preparation: i18n.t('payroll_bundle.policy_period.status.preparation'),
    supplements_definition: i18n.t('payroll_bundle.policy_period.status.supplements_definition'),
    under_review: i18n.t('payroll_bundle.policy_period.status.under_review'),
    payroll_calculation: i18n.t('payroll_bundle.policy_period.status.payroll_calculation'),
    payment_processing: i18n.t('payroll_bundle.policy_period.status.payment_processing'),
    paid: i18n.t('payroll_bundle.policy_period.status.paid'),
  })

  static StatusDescriptions = (): StatusDescription => ({
    preparation: {
      main_description: i18n.t('payroll_bundle.policy_period.status.main_description.preparation'),
      modal_description: i18n.t(
        'payroll_bundle.policy_period.status.modal_description.preparation'
      ),
    },
    supplements_definition: {
      main_description: i18n.t(
        'payroll_bundle.policy_period.status.main_description.supplements_definition'
      ),
      modal_description: i18n.t(
        'payroll_bundle.policy_period.status.modal_description.supplements_definition'
      ),
      notification: i18n.t('payroll_bundle.policy_period.status.modal.notify_colaborators'),
    },
    under_review: {
      main_description: i18n.t('payroll_bundle.policy_period.status.main_description.under_review'),
      modal_description: i18n.t(
        'payroll_bundle.policy_period.status.modal_description.under_review'
      ),
      notification: i18n.t('payroll_bundle.policy_period.status.modal.notify_colaborators'),
    },
    payroll_calculation: {
      main_description: i18n.t(
        'payroll_bundle.policy_period.status.main_description.payroll_calculation'
      ),
      modal_description: i18n.t(
        'payroll_bundle.policy_period.status.modal_description.payroll_calculation'
      ),
      additional_modal_description: i18n.t(
        'payroll_bundle.policy_period.status.additional_modal_description.payroll_calculation'
      ),
    },
    payment_processing: {
      main_description: i18n.t(
        'payroll_bundle.policy_period.status.main_description.payment_processing'
      ),
      modal_description: i18n.t(
        'payroll_bundle.policy_period.status.modal_description.payment_processing'
      ),
    },
    paid: {
      main_description: i18n.t('payroll_bundle.policy_period.status.main_description.paid'),
      modal_description: i18n.t('payroll_bundle.policy_period.status.modal_description.paid'),
    },
  })

  static urlRoot() {
    return '/payroll/policy_periods'
  }

  // Assumes that periods are ordered from older to later
  static findCurrentOrLatestPeriodIndex(periods: PolicyPeriod[]): number {
    const currentPeriodIndex = periods.findIndex((period) =>
      moment(period.attributes.ends_on).isSameOrAfter(moment(), 'day')
    )

    return currentPeriodIndex === -1 && periods.length > 0 ? periods.length - 1 : currentPeriodIndex
  }

  static findPreviousOrLatestPeriodIndex(periods: PolicyPeriod[]): number {
    const currentPeriodIndex = PolicyPeriod.findCurrentOrLatestPeriodIndex(periods)
    if (currentPeriodIndex === -1 && periods.length > 0) {
      return periods.length - 2
    } else {
      return currentPeriodIndex - 1
    }
  }

  static sortPolicyPeriodsFromOlderToNewer(policyPeriods: PolicyPeriod[]): PolicyPeriod[] {
    return policyPeriods.sort(
      (a, b) => moment(a.attributes.starts_on).valueOf() - moment(b.attributes.starts_on).valueOf()
    )
  }

  async changeStatus(data: {
    status: string
    notify_employee: boolean
    employee_ids: number[]
  }): Promise<void> {
    const result = await this.rpc('change_status', data)
    this.set(result)
  }

  statusAuthorizations(): Promise<PolicyPeriodAuthorizations> {
    return this.rpc('status_authorizations')
  }

  get isPayrollCalculation() {
    return this.attributes.status === PayrollStatus.PAYROLL_CALCULATION
  }
}

export default PolicyPeriod
